import { Alert, Box, Paper } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { adminApi } from "apis";
import {
  PracticeUserSearch,
  PracticeUsersLoadingSkeleton,
} from "components/features/practiceManagement";
import { DashboardLayout } from "components/layouts";
import { EPath } from "constants/path";
import {
  GetPracticesListRequest,
  GetPracticesListSortByEnum,
  GetPracticesListSortOrderEnum,
  PracticeListResponseDto,
} from "libs/cellera-sdk";
import { useCelleraTheme } from "theme/useCelleraTheme";
import PracticeTable, {
  Practice,
} from "components/features/practiceManagement/PracticeTable";

interface PracticeFiltersState {
  search: string;
  city: string;
  sortBy: GetPracticesListSortByEnum;
  sortOrder: GetPracticesListSortOrderEnum;
  page: number;
  pageSize: number;
}

const DEFAULT_SORT_BY = GetPracticesListSortByEnum.Name;
const DEFAULT_SORT_ORDER = GetPracticesListSortOrderEnum.Asc;
const DEFAULT_PAGE_SIZE = 10;

const PracticeProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { colors } = useCelleraTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<PracticeFiltersState>(() => ({
    search: searchParams.get("search") ?? "",
    city: searchParams.get("city") ?? "",
    sortBy:
      (searchParams.get("sortBy") as GetPracticesListSortByEnum) ??
      DEFAULT_SORT_BY,
    sortOrder:
      (searchParams.get("sortOrder") as GetPracticesListSortOrderEnum) ??
      DEFAULT_SORT_ORDER,
    page: Number(searchParams.get("page") ?? "1"),
    pageSize: Number(searchParams.get("pageSize") ?? DEFAULT_PAGE_SIZE),
  }));

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    const newParams = new URLSearchParams();

    if (filters.search) newParams.set("search", filters.search);
    if (filters.city) newParams.set("city", filters.city);
    if (filters.sortBy !== DEFAULT_SORT_BY)
      newParams.set("sortBy", filters.sortBy);
    if (filters.sortOrder !== DEFAULT_SORT_ORDER)
      newParams.set("sortOrder", filters.sortOrder);
    if (filters.page !== 1) newParams.set("page", String(filters.page));
    if (filters.pageSize !== DEFAULT_PAGE_SIZE)
      newParams.set("pageSize", String(filters.pageSize));

    setSearchParams(newParams, { replace: true });
  }, [filters, setSearchParams]);

  const queryParams = useMemo(
    (): GetPracticesListRequest => ({
      search: debouncedSearch || undefined,
      city: filters.city || undefined,
      sortBy: (filters.sortBy as GetPracticesListSortByEnum) || undefined,
      sortOrder:
        (filters.sortOrder as GetPracticesListSortOrderEnum) || undefined,
      page: filters.page,
      pageSize: filters.pageSize,
    }),
    [
      debouncedSearch,
      filters.page,
      filters.pageSize,
      filters.city,
      filters.sortBy,
      filters.sortOrder,
    ],
  );

  const { data, isLoading, isFetching, error, refetch } = useQuery<
    PracticeListResponseDto,
    Error
  >({
    queryKey: ["practices", queryParams],
    queryFn: () => adminApi.getAllPractices(queryParams),
    placeholderData: (previousData) => previousData,
    staleTime: 5 * 60 * 1000,
  });

  const practices = useMemo<Practice[]>(() => {
    if (!data?.practices) return [];

    return data.practices.map((practice) => ({
      id: practice.id,
      name: practice.name,
      location: practice.location,
      specialties: practice.specialties || [],
    }));
  }, [data?.practices]);

  const totalCount = data?.pagination?.total ?? 0;

  const handleSearchChange = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  const handleSortChange = useCallback(
    (sortBy: string, sortOrder: GetPracticesListSortOrderEnum) => {
      setFilters((prev) => ({
        ...prev,
        sortBy: sortBy as GetPracticesListSortByEnum,
        sortOrder,
      }));
    },
    [],
  );

  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setFilters((prev) => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handlePracticeClick = useCallback(
    (practiceId: string) => {
      navigate(EPath.PRACTICE_DETAIL.replace(":practiceId", practiceId));
    },
    [navigate],
  );

  return (
    <DashboardLayout title="Practice Profile">
      <Box sx={{ p: 2 }}>
        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Box
                component="button"
                onClick={() => refetch()}
                style={{
                  background: "none",
                  border: "none",
                  color: colors.primary.main,
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Retry
              </Box>
            }
          >
            {error instanceof Error
              ? error.message
              : "Failed to load practices"}
          </Alert>
        ) : null}

        <Paper sx={{ border: "1px solid #E5E7EB", borderRadius: 2, p: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: { xs: "stretch", md: "center" },
              mb: 3,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <PracticeUserSearch
                value={filters.search}
                onChange={handleSearchChange}
              />
            </Box>
          </Box>

          {isLoading && practices.length === 0 ? (
            <PracticeUsersLoadingSkeleton />
          ) : (
            <PracticeTable
              practices={practices}
              loading={isFetching}
              sortBy={filters.sortBy as string}
              sortOrder={filters.sortOrder}
              onSortChange={handleSortChange}
              onPracticeClick={handlePracticeClick}
              pagination={{
                page: filters.page,
                pageSize: filters.pageSize,
                total: totalCount,
                onPageChange: handlePageChange,
                onPageSizeChange: handlePageSizeChange,
              }}
            />
          )}
        </Paper>
      </Box>
    </DashboardLayout>
  );
};

export default PracticeProfilePage;
